import { useEffect, useState } from "react";
import { Search, Package } from "lucide-react";
import { api } from "../api";
import { SectionReveal } from "../components/Reveal";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  completed: "text-emerald-400 border-emerald-400/40",
  refunded: "text-amber-400 border-amber-400/40",
};

export default function Orders() {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.email) {
      setEmail(user.email);
      api
        .get("/orders", { params: { email: user.email } })
        .then((r) => setOrders(r.data))
        .catch(() => setOrders([]));
    }
  }, [user]);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get("/orders", { params: { email } });
      setOrders(data);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="orders-page" className="pt-16 min-h-screen">
      <div className="px-4 sm:px-8 lg:px-16 py-14 border-b border-zinc-900">
        <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase">Order Lookup</p>
        <h1 className="font-display font-extrabold tracking-tighter uppercase text-4xl sm:text-5xl lg:text-6xl mt-3">Your Orders</h1>
        <p className="text-zinc-400 text-sm mt-4 font-light max-w-md">
          Enter the email you used at checkout to see every order tied to it.
        </p>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 py-12">
        <form onSubmit={lookup} className="flex max-w-lg" data-testid="order-lookup-form">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="order-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="EMAIL USED AT CHECKOUT"
              className="w-full bg-transparent border border-zinc-700 pl-11 pr-4 py-4 mono text-xs tracking-[0.15em] placeholder:text-zinc-600 focus:outline-none focus:border-[#FF334B]"
            />
          </div>
          <button
            data-testid="order-lookup-button"
            disabled={loading}
            className="bg-[#FF334B] text-white mono text-xs tracking-[0.25em] uppercase px-8 hover:bg-[#d92339] transition-colors disabled:opacity-50"
          >
            {loading ? "…" : "Find"}
          </button>
        </form>

        {orders !== null && (
          <div className="mt-12 max-w-3xl">
            {orders.length === 0 ? (
              <p data-testid="orders-empty-message" className="text-zinc-500 text-sm">
                No paid orders found for that email yet.
              </p>
            ) : (
              orders.map((o) => (
                <SectionReveal key={o.id}>
                  <div data-testid={`order-card-${o.id.slice(0, 8)}`} className="border border-zinc-800 bg-[#121215] p-6 sm:p-8 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-5">
                      <div className="flex items-center gap-4">
                        <Package size={20} className="text-[#FF334B]" />
                        <div>
                          <p className="mono text-sm tracking-[0.15em]">#{o.id.slice(0, 8).toUpperCase()}</p>
                          <p className="mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase mt-1">
                            {new Date(o.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                          </p>
                        </div>
                      </div>
                      <span
                        data-testid={`order-status-${o.id.slice(0, 8)}`}
                        className={`mono text-[10px] tracking-[0.25em] uppercase border px-3 py-1.5 ${STATUS_STYLES[o.status] || "text-zinc-400 border-zinc-600"}`}
                      >
                        {o.status === "completed" ? "Processing" : o.status}
                      </span>
                    </div>
                    <div className="py-4">
                      {o.items.map((i) => (
                        <div key={`${i.slug}-${i.size}`} className="flex items-center gap-4 py-2">
                          <img src={i.image} alt={i.name} className="w-12 h-14 object-cover bg-zinc-900" />
                          <div className="flex-1">
                            <p className="text-sm">{i.name}</p>
                            <p className="mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Size {i.size} × {i.qty}</p>
                          </div>
                          <p className="mono text-sm text-zinc-300">${(i.unit_price * i.qty).toFixed(0)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between border-t border-zinc-800 pt-4">
                      <span className="mono text-xs tracking-[0.25em] uppercase text-zinc-400">Total</span>
                      <span className="mono">${o.amount.toFixed(0)} USD</span>
                    </div>
                  </div>
                </SectionReveal>
              ))
            )}
          </div>
        )}
      </div>
    </main>
  );
}
